export class AudioManager {
  private static instance: AudioManager;
  private soundVolume: number = 50;
  private musicVolume: number = 50;
  private _isMuted: boolean = false;
  
  // Audio elements - using relative paths for itch.io compatibility
  private clickSounds: HTMLAudioElement[] = [];
  private effectSounds: HTMLAudioElement[] = [];
  private majorEventSound: HTMLAudioElement | null = null;
  private backgroundMusic: HTMLAudioElement | null = null;

  private constructor() {
    this.initializeAudio();
  }

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  private initializeAudio(): void {
    // Initialize click sounds - load all available variations for random selection
    const clickFiles = ['click (1).wav', 'click (2).wav', 'click (3).wav'];
    clickFiles.forEach((filename) => {
      try {
        const clickPath = `./audio/${filename}`.replace(/ /g, '%20');
        const audio = new Audio(clickPath);
        audio.preload = 'auto';
        audio.volume = this.soundVolume / 100;
        // Handle load errors
        audio.addEventListener('error', () => {
          console.warn(`Could not load click sound ${filename}: file not found or unsupported format`);
        });
        this.clickSounds.push(audio);
      } catch (error) {
        console.warn(`Could not load click sound ${filename}:`, error);
      }
    });

    // Initialize effect sounds - load all available variations for random selection
    const effectFiles = ['effect (1).wav', 'effect (2).wav'];
    effectFiles.forEach((filename) => {
      try {
        const effectPath = `./audio/${filename}`.replace(/ /g, '%20');
        const audio = new Audio(effectPath);
        audio.preload = 'auto';
        audio.volume = this.soundVolume / 100;
        // Handle load errors
        audio.addEventListener('error', () => {
          console.warn(`Could not load effect sound ${filename}: file not found or unsupported format`);
        });
        this.effectSounds.push(audio);
      } catch (error) {
        console.warn(`Could not load effect sound ${filename}:`, error);
      }
    });

    // Initialize major event sound
    try {
      this.majorEventSound = new Audio('./audio/major_modal.wav');
      this.majorEventSound.preload = 'auto';
      this.majorEventSound.volume = this.soundVolume / 100;
      // Handle load errors
      this.majorEventSound.addEventListener('error', () => {
        console.warn('Could not load major event sound: file not found or unsupported format');
        this.majorEventSound = null;
      });
    } catch (error) {
      console.warn('Could not load major event sound:', error);
      this.majorEventSound = null;
    }

    // Initialize background music
    try {
      this.backgroundMusic = new Audio('./audio/anomaly_research_facility.mp3');
      this.backgroundMusic.preload = 'auto';
      this.backgroundMusic.loop = true;
      this.backgroundMusic.volume = this.musicVolume / 100;
      // Handle load errors
      this.backgroundMusic.addEventListener('error', () => {
        console.warn('Could not load background music: file not found or unsupported format');
        this.backgroundMusic = null;
      });
    } catch (error) {
      console.warn('Could not load background music:', error);
      this.backgroundMusic = null;
    }

    // Load volume settings from localStorage
    const savedSoundVolume = localStorage.getItem('soundVolume');
    const savedMusicVolume = localStorage.getItem('musicVolume');
    const savedMuted = localStorage.getItem('isMuted');

    if (savedSoundVolume !== null) {
      this.soundVolume = parseInt(savedSoundVolume, 10);
      this.updateSoundVolume(this.soundVolume);
    }

    if (savedMusicVolume !== null) {
      this.musicVolume = parseInt(savedMusicVolume, 10);
      this.updateMusicVolume(this.musicVolume);
    }

    if (savedMuted === 'true') {
      this._isMuted = true;
    }
  }

  private updateSoundVolume(volume: number): void {
    const volumeValue = (this._isMuted ? 0 : volume) / 100;
    this.clickSounds.forEach(sound => {
      sound.volume = volumeValue;
    });
    this.effectSounds.forEach(sound => {
      sound.volume = volumeValue;
    });
    if (this.majorEventSound) {
      this.majorEventSound.volume = volumeValue;
    }
  }

  private updateMusicVolume(volume: number): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.volume = (this._isMuted ? 0 : volume) / 100;
    }
  }

  playClick(): void {
    if (this.clickSounds.length === 0 || this._isMuted) {
      return;
    }
    
    // Randomly select one of the available click sounds
    const randomIndex = Math.floor(Math.random() * this.clickSounds.length);
    const selectedSound = this.clickSounds[randomIndex];
    
    // Check if audio has a valid source before playing
    if (selectedSound.readyState === HTMLMediaElement.HAVE_NOTHING) {
      // Audio hasn't loaded yet, skip silently
      return;
    }
    
    selectedSound.currentTime = 0;
    selectedSound.play().catch(error => {
      // Silently handle playback errors (e.g., user interaction required, file not found)
      if (error.name !== 'NotSupportedError') {
        console.warn('Could not play click sound:', error);
      }
    });
  }

  playEffect(): void {
    if (this.effectSounds.length === 0 || this._isMuted) {
      return;
    }
    
    // Randomly select one of the available effect sounds
    const randomIndex = Math.floor(Math.random() * this.effectSounds.length);
    const selectedSound = this.effectSounds[randomIndex];
    
    // Check if audio has a valid source before playing
    if (selectedSound.readyState === HTMLMediaElement.HAVE_NOTHING) {
      // Audio hasn't loaded yet, skip silently
      return;
    }
    
    selectedSound.currentTime = 0;
    selectedSound.play().catch(error => {
      // Silently handle playback errors (e.g., user interaction required, file not found)
      if (error.name !== 'NotSupportedError') {
        console.warn('Could not play effect sound:', error);
      }
    });
  }

  playMajorEvent(): void {
    if (this.majorEventSound && !this._isMuted) {
      // Check if audio has a valid source before playing
      if (this.majorEventSound.readyState === HTMLMediaElement.HAVE_NOTHING) {
        // Audio hasn't loaded yet, skip silently
        return;
      }
      this.majorEventSound.currentTime = 0;
      this.majorEventSound.play().catch(error => {
        // Silently handle playback errors (e.g., user interaction required, file not found)
        if (error.name !== 'NotSupportedError') {
          console.warn('Could not play major event sound:', error);
        }
      });
    }
  }

  getSoundVolume(): number {
    return this.soundVolume;
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  setSoundVolume(volume: number): void {
    this.soundVolume = Math.max(0, Math.min(100, volume));
    this.updateSoundVolume(this.soundVolume);
    localStorage.setItem('soundVolume', this.soundVolume.toString());
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(100, volume));
    this.updateMusicVolume(this.musicVolume);
    localStorage.setItem('musicVolume', this.musicVolume.toString());
  }

  toggleMute(): void {
    this._isMuted = !this._isMuted;
    localStorage.setItem('isMuted', this._isMuted.toString());
    this.updateSoundVolume(this.soundVolume);
    this.updateMusicVolume(this.musicVolume);
  }

  isMuted(): boolean {
    return this._isMuted;
  }

  startBackgroundMusic(): void {
    if (this.backgroundMusic && !this._isMuted) {
      // Check if audio has a valid source before playing
      if (this.backgroundMusic.readyState === HTMLMediaElement.HAVE_NOTHING) {
        // Wait for the audio to load, then play
        this.backgroundMusic.addEventListener('canplay', () => {
          this.backgroundMusic!.play().catch(error => {
            // Handle autoplay policy restrictions
            if (error.name === 'NotAllowedError') {
              console.log('Background music autoplay blocked. Music will start after user interaction.');
            } else {
              console.warn('Could not play background music:', error);
            }
          });
        }, { once: true });
      } else {
        // Audio is ready, play it
        this.backgroundMusic.play().catch(error => {
          // Handle autoplay policy restrictions
          if (error.name === 'NotAllowedError') {
            console.log('Background music autoplay blocked. Music will start after user interaction.');
          } else {
            console.warn('Could not play background music:', error);
          }
        });
      }
    }
  }

  stopBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
    }
  }
}
